import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { eachDayOfInterval, format, parseISO, startOfMonth } from 'date-fns';

import { useLogs } from '@codes/logs';

import {
    DailyUsageChart,
    FilterSection,
    HourlyActivityChart,
    PerformanceMetricsTable,
    ServiceUsageChart,
    StatisticsCards,
} from '../components';

interface LogEntry {
    id: string;
    userId: string;
    userName: string;
    loginAt: string;
    logoutAt: string;
    duration: number;
    apiCallCount: number;
    avgResponseTime: number;
    errorCount: number;
    usedServices: string[];
}

const formatDuration = (seconds: number, t: any): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
        return t('logs.duration.hoursMinutes', { hours, minutes });
    }
    return t('logs.duration.minutes', { minutes });
};

export const LogsPage: React.FC = () => {
    const { t } = useTranslation();
    const [selectedCompany, setSelectedCompany] = useState('1000046');

    const getMinStartDate = (companyId: string): Date => {
        if (companyId === '1000049') {
            return new Date(2025, 7, 1); // 8월 1일 (month는 0부터 시작)
        }
        if (companyId === '1000061') {
            return new Date(2025, 10, 1); // 11월 1일 (month는 0부터 시작)
        }
        // 쓰리얼라이언스 2025-11-01~2025-12-31
        if (companyId === '1000066') {
            return new Date(2025, 10, 1); // 11월 1일 (month는 0부터 시작)
        }
        // 유니언하우스 2025-04-01~2025-11-30
        if (companyId === '1000067') {
            return new Date(2025, 3, 1); // 04월 1일 (month는 0부터 시작)
        }
        // 오늘카드 2025-11-01~2025-12-31
        if (companyId === '1000068') {
            return new Date(2025, 10, 1); // 11월 1일 (month는 0부터 시작)
        }
        return new Date(2025, 5, 1);
    };

    const getMaxEndDate = (companyId: string): Date => {
        const today = new Date();

        // 쓰리얼라이언스 2025-11-01~2025-12-31 (단, 오늘이 우선)
        if (companyId === '1000066') {
            const companyMax = new Date(2025, 11, 31); // 12월 31일 (month는 0부터 시작)
            return companyMax < today ? companyMax : today;
        }
        // 유니언하우스 2025-04-01~2025-11-30 (단, 오늘이 우선)
        if (companyId === '1000067') {
            const companyMax = new Date(2025, 10, 30); // 11월 30일 (month는 0부터 시작)
            return companyMax < today ? companyMax : today;
        }
        // 오늘카드 2025-11-01~2025-12-31 (단, 오늘이 우선)
        if (companyId === '1000068') {
            const companyMax = new Date(2025, 11, 31); // 12월 31일 (month는 0부터 시작)
            return companyMax < today ? companyMax : today;
        }
        // 나머지는 당일까지
        return today;
    };

    const [startDate, setStartDate] = useState<Date>(startOfMonth(getMinStartDate('1000046')));
    const [endDate, setEndDate] = useState<Date>(getMaxEndDate('1000046'));
    const [refreshKey, setRefreshKey] = useState(0);

    const { data, isFetching } = useLogs({
        userId: selectedCompany,
        from: format(startDate, 'yyyy-MM-dd'),
        to: format(endDate, 'yyyy-MM-dd'),
        key: refreshKey,
    });

    const statistics = useMemo(() => {
        const logList = data?.data || data?.list || [];
        if (!logList || logList.length === 0) {
            return {
                totalSessions: 0,
                totalApiCalls: 0,
                totalDuration: t('logs.duration.hours', { hours: 0 }),
                avgResponseTime: `0${t('logs.table.timeUnit.milliseconds')}`,
            };
        }

        const totalSessions = logList.length;
        // Only count API calls from logs that have associated services to match the Performance Metrics table
        const totalApiCalls = logList
            .filter((log: LogEntry) => log.usedServices.length > 0)
            .reduce((sum: number, log: LogEntry) => sum + log.apiCallCount, 0);
        const totalSeconds = logList.reduce((sum: number, log: LogEntry) => sum + log.duration, 0);

        const serviceUsage: Record<string, { count: number; avgResponse: number }> = {};
        logList.forEach((log: LogEntry) => {
            const servicesCount = log.usedServices.length;
            if (servicesCount === 0) {
                return;
            }

            log.usedServices.forEach(service => {
                if (!serviceUsage[service]) {
                    serviceUsage[service] = { count: 0, avgResponse: 0 };
                }
                serviceUsage[service].count += 1;
                serviceUsage[service].avgResponse += log.avgResponseTime;
            });
        });

        const apiAvgResponseTimes = Object.values(serviceUsage).map(data => Math.round(data.avgResponse / data.count));

        const avgResponseTime =
            apiAvgResponseTimes.length > 0
                ? Math.round(apiAvgResponseTimes.reduce((sum, avg) => sum + avg, 0) / apiAvgResponseTimes.length)
                : 0;

        return {
            totalSessions,
            totalApiCalls,
            totalDuration: formatDuration(totalSeconds, t),
            avgResponseTime: `${avgResponseTime}${t('logs.table.timeUnit.milliseconds')}`,
        };
    }, [data, t]);

    const apiCallData = useMemo(() => {
        const logList = data?.data || data?.list || [];
        if (!logList || logList.length === 0) {
            return [];
        }

        const serviceUsage: Record<string, { count: number; apiCalls: number; avgResponse: number }> = {};

        logList.forEach((log: LogEntry) => {
            const servicesCount = log.usedServices.length;
            if (servicesCount === 0) {
                return;
            }

            const baseApiCalls = Math.floor(log.apiCallCount / servicesCount);
            const remainder = log.apiCallCount % servicesCount;

            log.usedServices.forEach((service, index) => {
                if (!serviceUsage[service]) {
                    serviceUsage[service] = { count: 0, apiCalls: 0, avgResponse: 0 };
                }
                serviceUsage[service].count += 1;
                serviceUsage[service].apiCalls += baseApiCalls + (index < remainder ? 1 : 0);
                serviceUsage[service].avgResponse += log.avgResponseTime;
            });
        });

        const result = Object.entries(serviceUsage).map(([service, data]) => ({
            name: service.replace('lemon-', '').replace('-api', ''),
            fullName: service,
            [t('logs.charts.usageCount')]: data.count,
            [t('logs.charts.apiCallLabel')]: data.apiCalls,
            [t('logs.charts.avgResponseTimeLabel')]: Math.round(data.avgResponse / data.count),
        }));

        return result;
    }, [data, t]);

    const dailyUsageData = useMemo(() => {
        const logList = data?.data || data?.list || [];

        // Generate all dates in the selected range
        const allDates = eachDayOfInterval({
            start: startDate,
            end: endDate,
        });

        // Initialize all dates with zero values
        const dailyData: Record<
            string,
            { date: string; sessions: number; apiCalls: number; duration: number; errors: number }
        > = {};

        allDates.forEach(date => {
            const dateKey = format(date, 'MM-dd');
            dailyData[dateKey] = { date: dateKey, sessions: 0, apiCalls: 0, duration: 0, errors: 0 };
        });

        // Fill in actual log data
        if (logList && logList.length > 0) {
            logList.forEach((log: LogEntry) => {
                const date = format(parseISO(log.loginAt), 'MM-dd');
                if (dailyData[date]) {
                    dailyData[date].sessions += 1;
                    dailyData[date].apiCalls += log.apiCallCount;
                    dailyData[date].duration += Math.round(log.duration / 60);
                    dailyData[date].errors += log.errorCount;
                }
            });
        }

        return Object.values(dailyData).sort((a, b) => a.date.localeCompare(b.date));
    }, [data, startDate, endDate]);

    const hourlyActivityData = useMemo(() => {
        const logList = data?.data || data?.list || [];
        if (!logList || logList.length === 0) {
            return [];
        }

        const hourlyData: Record<number, number> = {};
        for (let i = 0; i < 24; i++) {
            hourlyData[i] = 0;
        }

        logList.forEach((log: LogEntry) => {
            const hour = parseInt(format(parseISO(log.loginAt), 'HH'));
            hourlyData[hour] += 1;
        });

        return Object.entries(hourlyData).map(([hour, count]) => ({
            hour: t('logs.charts.hourLabel', { hour }),
            [t('logs.charts.activityCount')]: count,
        }));
    }, [data, t]);

    const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];
    const GRADIENT_COLORS = [
        { start: '#667eea', end: '#764ba2' },
        { start: '#f093fb', end: '#f5576c' },
        { start: '#4facfe', end: '#00f2fe' },
        { start: '#43e97b', end: '#38f9d7' },
    ];

    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };

    if (isFetching) {
        return (
            <div className="animate-pulse space-y-4">
                <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                <div className="h-32 bg-gray-200 rounded"></div>
            </div>
        );
    }

    return (
        <div className="flex-1 flex bg-background w-full">
            <div className="flex-1 min-h-0 w-full h-full py-6 px-[68px] max-md:px-5 space-y-[26px]">
                <FilterSection
                    selectedCompany={selectedCompany}
                    setSelectedCompany={setSelectedCompany}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    getMinStartDate={getMinStartDate}
                    getMaxEndDate={getMaxEndDate}
                    handleRefresh={handleRefresh}
                    isFetching={isFetching}
                />

                <StatisticsCards statistics={statistics} />

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-[26px]">
                    <ServiceUsageChart data={apiCallData} colors={COLORS} gradientColors={GRADIENT_COLORS} />

                    <DailyUsageChart data={dailyUsageData} />

                    <HourlyActivityChart data={hourlyActivityData} />

                    <PerformanceMetricsTable data={apiCallData} colors={COLORS} />
                </div>

                {/* 로그 콘솔 */}
                {/*<LogConsole*/}
                {/*    groupedLogs={groupedLogs}*/}
                {/*    expandedDates={expandedDates}*/}
                {/*    toggleDateExpansion={toggleDateExpansion}*/}
                {/*    formatDuration={formatDuration}*/}
                {/*/>*/}
            </div>
        </div>
    );
};
